import { useState, useEffect, useCallback } from 'react';

import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';

import {
  BillingType,
  CardApproval,
  CardApprovalRequest,
  CardBillingRequest,
  deleteMappingForBillingItem,
  getBillingMappingTransactions,
  getBillingsForMapping,
  getCardApprovals,
  getCardApprovalByCursor,
  getCardBillingItem,
  getCardBillingTransactions,
  getCardList,
  getScrapeSpecForBillings,
  getScrapeSpecForPrePayment,
  getTransactionMappingBillings,
  labelCardBillingItem,
  updateTransactionMappingBillings,
  CardApprovalQueryParams,
  getCardApprovalById,
  getPurchaseMappedByApproval,
  CardPurchaseRequest,
  getCardPurchases,
  getApprovalMappedByPurchase,
  patchChangeCardAlias,
  getUnifiedCardTransactions,
  UnifiedCardTransactionRequest,
  labelUnifiedCardTransaction,
  getCardBusinessEntities,
} from '~apis/card';
import {
  createCardBillingMemo,
  upsertCardApprovalMemo,
  updateCardBillingMemo,
  upsertCardPurchaseMemo,
  upsertCardTransactionMemo,
} from '~apis/card/memos';
import postConnect from '~apis/corporate-card/postConnect';
import { IntegratedScrapeQueryStatusKeys } from '~hooks/queries/scraping/useGetIntegratedScrapeStatus';
import { CASH_FLOW_KEY } from '~hooks/queries/useGetCashFlow';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { queryClient } from '~libs/queryClient';
import { CardLoginByIdRequest } from '~models/card';
import { mixpanel } from '~pages/_app';

import { HomeQueryKey } from '../home/query';
import { OrganizationsQueryKey } from '../organizations/query';
import { TransactionsQueryKey } from '../transactions/query';
import { USE_BUSINESS_REGISTRATION_NUMBERS_QUERY } from '../useBusinessRegistrationNumbers';
import { USE_GET_CORPORATE_CARD_CONNECTIONS_QUERY } from '../useCorporateCardConnections';
import { USE_GET_CORPORATE_CARD_LIST_QUERY } from '../useGetCorporateCardList';

export const afterCardLabeling = (companyId: string) => {
  queryClient.invalidateQueries(CARD_QUERY_KEY.billing(companyId));
  queryClient.invalidateQueries(CASH_FLOW_KEY.default);
};

export const CARD_QUERY_KEY = {
  default: ['card'],
  approval: ['approval'],
  purchase: ['purchase'],
  unifiedTransaction: (companyId: string, request?: UnifiedCardTransactionRequest) =>
    [...CARD_QUERY_KEY.default, 'unifiedTransaction', companyId, request].filter(Boolean),
  billing: (companyId: string, request?: CardBillingRequest) =>
    ['card', 'billing', companyId, request].filter(Boolean),
  item: (companyId: string, itemId: number) => ['card', 'item', companyId, itemId],
  specForBillings: (companyId: string) => ['card', 'specForBillings', companyId],
  specForPrePayments: (companyId: string) => ['card', 'specForPrePayments', companyId],
  billingForMapping: (
    companyId: string,
    startDate: string,
    endDate: string,
    transactionId: number,
    forceAllOrganizations?: boolean,
  ) => [
    'card',
    'billingForMapping',
    companyId,
    startDate,
    endDate,
    transactionId,
    forceAllOrganizations,
  ],
  cardListForOptions: (companyId: string) => ['card', 'listForOptions', companyId],
  billingMappingTransaction: (companyId: string, billingItemId: number) => [
    'card',
    'billingMappingTransaction',
    companyId,
    billingItemId,
  ],
  transactionMappingBillings: (companyId: string, transactionId: number) => [
    'card',
    'transactionMappingBillings',
    companyId,
    transactionId,
  ],
  approvalMappedByPurchase: (companyId: string, purchaseId?: number) => [
    'purchase',
    companyId,
    purchaseId,
  ],
  purchaseMappedByApproval: (companyId: string, approvalId?: number) => [
    'purchase',
    companyId,
    approvalId,
  ],
  cardBusinessEntities: (companyId: string) => ['card', 'businessEntities', companyId],
};

export function loginCardByID() {
  const queryClient = useQueryClient();
  return useMutation((payload: CardLoginByIdRequest) => postConnect(payload), {
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(OrganizationsQueryKey.default());
      queryClient.invalidateQueries(USE_GET_CORPORATE_CARD_CONNECTIONS_QUERY.root);
      queryClient.invalidateQueries(USE_GET_CORPORATE_CARD_LIST_QUERY.root);
      queryClient.invalidateQueries(
        IntegratedScrapeQueryStatusKeys.scrapeStatus(variables.companyId),
      );
      queryClient.invalidateQueries(USE_BUSINESS_REGISTRATION_NUMBERS_QUERY.root);
    },
  });
}

export function useGetCardApprovals({
  companyId,
  request,
  enabled = true,
}: {
  companyId: string;
  request: CardApprovalRequest;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: [
      'approval',
      companyId,
      request.startDate,
      request.endDate,
      request.sort,
      request.cardNos,
      request.cancelTypes,
      request.paymentTypes,
      request.homeForeignTypes,
      request.searchParam,
      request.page,
      request.size,
    ],
    queryFn: () =>
      getCardApprovals({
        companyId,
        request,
      }),
    enabled: !!companyId && enabled,
  });
}

export function useGetCardApprovalById({
  companyId,
  approvalId,
  trigger,
}: {
  companyId: string;
  approvalId: number;
  trigger?: boolean;
}) {
  return useQuery({
    queryKey: [...CARD_QUERY_KEY.approval, companyId, approvalId],
    queryFn: () => getCardApprovalById({ companyId, approvalId }),
    enabled: !!companyId && (trigger ?? true),
  });
}

export function useGetApprovalMappedByPurchase({
  companyId,
  purchaseId,
}: {
  companyId: string;
  purchaseId: number;
}) {
  return useQuery({
    queryKey: CARD_QUERY_KEY.approvalMappedByPurchase(companyId, purchaseId),
    queryFn: () => getApprovalMappedByPurchase({ companyId, purchaseId }),
    enabled: !!companyId && !!purchaseId,
  });
}

export function useGetCardPurchases({
  companyId,
  request,
  enabled = true,
}: {
  companyId: string;
  request: CardPurchaseRequest;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: [
      'purchase',
      companyId,
      request.startDate,
      request.endDate,
      request.sort,
      request.cardNos,
      request.cancelTypes,
      request.paymentTypes,
      request.homeForeignTypes,
      request.searchParam,
      request.page,
      request.size,
    ],
    queryFn: () =>
      getCardPurchases({
        companyId,
        request,
      }),
    enabled: !!companyId && enabled,
  });
}

export function useGetCardApprovalsInfinite({
  companyId,
  request,
}: {
  companyId: string;
  request: Omit<CardApprovalRequest, 'page'>;
}) {
  return useInfiniteQuery({
    queryKey: [
      'approval',
      'infinite',
      companyId,
      request.startDate,
      request.endDate,
      request.sort,
      request.cardNos,
      request.cancelTypes,
      request.paymentTypes,
      request.searchParam,
    ],
    queryFn: ({ pageParam = 0 }) =>
      getCardApprovals({
        companyId,
        request: {
          ...request,
          page: pageParam,
        },
      }),
    getNextPageParam: (lastPage, pages) => {
      const totalPages = lastPage.Data?.data?.totalPages ?? 0;
      const nextPage = pages.length;
      return nextPage < totalPages ? nextPage : undefined;
    },
    enabled: !!companyId,
  });
}

export function useUpsertCardApprovalMemo() {
  return useMutation({
    mutationFn: upsertCardApprovalMemo,
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries(CARD_QUERY_KEY.approval);
      queryClient.invalidateQueries(CARD_QUERY_KEY.purchase);
      queryClient.invalidateQueries([...CARD_QUERY_KEY.default, variables.companyId]);
    },
  });
}

export function useUpsertCardPurchaseMemo() {
  return useMutation({
    mutationFn: upsertCardPurchaseMemo,
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries(CARD_QUERY_KEY.purchase);
      queryClient.invalidateQueries(CARD_QUERY_KEY.approval);
      queryClient.invalidateQueries([...CARD_QUERY_KEY.default, variables.companyId]);
    },
  });
}

const getCardBillingTransactionsEnabled = (request: CardBillingRequest) => {
  if (request.transactionId) {
    return true;
  }

  if (request.billingId) {
    return !!request.billingType;
  }

  if (request.rangeType) {
    return !!request.startDate && !!request.endDate;
  }
};

export function useGetCardBillingTransactions(companyId: string, request: CardBillingRequest) {
  return useQuery({
    queryKey: CARD_QUERY_KEY.billing(companyId, request),
    queryFn: () => getCardBillingTransactions({ companyId, request }),
    enabled: !!companyId && getCardBillingTransactionsEnabled(request),
    refetchOnMount: true,
  });
}

export function useGetUnifiedCardTransactions(
  companyId: string,
  request: UnifiedCardTransactionRequest,
) {
  return useQuery({
    queryKey: CARD_QUERY_KEY.unifiedTransaction(companyId, request),
    queryFn: () => getUnifiedCardTransactions({ companyId, request }),
    enabled: !!companyId && !!request.startDate && !!request.endDate && !!request.rangeType,
    refetchOnMount: true,
  });
}

export function useGetCardBillingItem(companyId: string, itemId: number) {
  return useQuery({
    queryKey: CARD_QUERY_KEY.item(companyId, itemId),
    queryFn: () => getCardBillingItem({ companyId, itemId }),
    enabled: !!companyId && !!itemId,
  });
}

export function useGetScrapeSpecForBilling() {
  const { companyId } = useDemoAwareCompanyId();
  return useQuery({
    queryKey: CARD_QUERY_KEY.specForBillings(companyId),
    queryFn: () => getScrapeSpecForBillings({ companyId }),
    enabled: !!companyId,
  });
}

export function useGetScrapeSpecForPrePayments() {
  const { companyId } = useDemoAwareCompanyId();
  return useQuery({
    queryKey: CARD_QUERY_KEY.specForPrePayments(companyId),
    queryFn: () => getScrapeSpecForPrePayment({ companyId }),
    enabled: !!companyId,
  });
}

export function useGetBillingsForMapping({
  startDate,
  endDate,
  transactionId,
  forceAllOrganizations,
}: {
  companyId: string;
  startDate: string;
  endDate: string;
  transactionId: number;
  forceAllOrganizations?: boolean;
}) {
  const { companyId } = useDemoAwareCompanyId();
  return useQuery({
    queryKey: CARD_QUERY_KEY.billingForMapping(
      companyId,
      startDate,
      endDate,
      transactionId,
      forceAllOrganizations,
    ),
    queryFn: () =>
      getBillingsForMapping({
        companyId,
        startDate,
        endDate,
        transactionId,
        forceAllOrganizations,
      }),
    refetchOnMount: true,
    enabled: !!companyId,
  });
}

export function useGetCardListForOptions() {
  const { companyId } = useDemoAwareCompanyId();
  return useQuery({
    queryKey: CARD_QUERY_KEY.cardListForOptions(companyId),
    queryFn: () => getCardList({ companyId }),
    enabled: !!companyId,
  });
}

export function useGetBillingMappingTransactions({
  companyId,
  billingItemId,
  billingType,
}: {
  companyId: string;
  billingItemId: number;
  billingType: BillingType;
}) {
  return useQuery({
    queryKey: CARD_QUERY_KEY.billingMappingTransaction(companyId, billingItemId),
    queryFn: () => getBillingMappingTransactions({ companyId, billingItemId, billingType }),
    enabled: !!companyId && !!billingItemId && !!billingType,
  });
}

export function useGetTransactionMappingBillings({
  companyId,
  transactionId,
}: {
  companyId: string;
  transactionId: number;
}) {
  return useQuery({
    queryKey: CARD_QUERY_KEY.transactionMappingBillings(companyId, transactionId),
    queryFn: () => getTransactionMappingBillings({ companyId, transactionId }),
    refetchOnMount: true,
    enabled: !!companyId && !!transactionId,
  });
}

export function useUpdateTransactionMappingBillings() {
  return useMutation({
    mutationFn: updateTransactionMappingBillings,
    onSuccess: async (_, variables) => {
      invalidateAllRelatedCaches(variables.companyId);
    },
  });
}

export function useLabellingCardBilling(doNotRefetch?: boolean) {
  const { companyId } = useDemoAwareCompanyId();
  return useMutation({
    mutationFn: labelCardBillingItem,
    onSuccess: async () => {
      if (doNotRefetch) {
        return;
      }
      queryClient.invalidateQueries(CARD_QUERY_KEY.billing(companyId));
      queryClient.invalidateQueries(CASH_FLOW_KEY.default);
    },
  });
}

export function useLabellingUnifiedCardTransaction(doNotRefetch?: boolean) {
  const { companyId } = useDemoAwareCompanyId();
  return useMutation({
    mutationFn: labelUnifiedCardTransaction,
    onSuccess: async () => {
      if (doNotRefetch) {
        return;
      }
      queryClient.invalidateQueries(CARD_QUERY_KEY.unifiedTransaction(companyId));
      queryClient.invalidateQueries(CASH_FLOW_KEY.default);
    },
  });
}

export function useGetPurchaseMappedByApproval({
  companyId,
  approvalId,
}: {
  companyId: string;
  approvalId: number;
}) {
  return useQuery({
    queryKey: CARD_QUERY_KEY.purchaseMappedByApproval(companyId, approvalId),
    queryFn: () => getPurchaseMappedByApproval({ companyId, approvalId }),
    enabled: !!companyId && !!approvalId,
  });
}

export function useCreateCardBillingMemo() {
  const { companyId } = useDemoAwareCompanyId();
  return useMutation({
    mutationFn: createCardBillingMemo,
    onSuccess: async () => {
      queryClient.invalidateQueries(CARD_QUERY_KEY.billing(companyId));
    },
  });
}

export function useUpdateCardBillingMemo() {
  const { companyId } = useDemoAwareCompanyId();
  return useMutation({
    mutationFn: updateCardBillingMemo,
    onSuccess: async () => {
      queryClient.invalidateQueries(CARD_QUERY_KEY.billing(companyId));
    },
  });
}

export function useDeleteCardBillingMapping() {
  return useMutation({
    mutationFn: deleteMappingForBillingItem,
    onSuccess: async (_, variables) => {
      invalidateAllRelatedCaches(variables.companyId);
    },
  });
}

export function useChangeCardAlias() {
  return useMutation({
    mutationFn: patchChangeCardAlias,
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries(OrganizationsQueryKey.info({ companyId: variables.companyId }));
      queryClient.invalidateQueries(HomeQueryKey.default());
      queryClient.invalidateQueries(CARD_QUERY_KEY.billing(variables.companyId));
      queryClient.invalidateQueries([...CARD_QUERY_KEY.approval, variables.companyId]);
      queryClient.invalidateQueries([...CARD_QUERY_KEY.purchase, variables.companyId]);
    },
  });
}

export function useGetCardBusinessEntities({ companyId }: { companyId: string }) {
  return useQuery({
    queryKey: CARD_QUERY_KEY.cardBusinessEntities(companyId),
    queryFn: () => getCardBusinessEntities({ companyId }),
    enabled: !!companyId,
    refetchOnMount: true,
  });
}

export function useUpsertCardTransactionMemo({ companyId }: { companyId: string }) {
  return useMutation({
    mutationFn: upsertCardTransactionMemo,
    onSuccess: async () => {
      queryClient.invalidateQueries(CARD_QUERY_KEY.unifiedTransaction(companyId));
    },
  });
}

function invalidateAllRelatedCaches(companyId: string) {
  queryClient.invalidateQueries(CARD_QUERY_KEY.default);
  queryClient.invalidateQueries(TransactionsQueryKey.list({ companyId }));
  queryClient.invalidateQueries(CASH_FLOW_KEY.default);
}

export function useInfiniteCardApprovalsByCursor({
  startDate,
  endDate,
  cardNos,
  size = 20,
  withNoDate,
}: {
  startDate?: string;
  endDate?: string;
  cardNos?: string[];
  size?: number;
  withNoDate?: boolean;
}) {
  const { companyId } = useDemoAwareCompanyId();
  const [allCardApprovals, setAllCardApprovals] = useState<CardApproval[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<{ usedAt: string; id: number } | null>(null);
  const [hasNext, setHasNext] = useState(false);

  // 초기 데이터 로드
  const initialQuery = useQuery({
    queryKey: ['cardApproval', 'infinite', companyId, startDate, endDate, cardNos, size],
    queryFn: () => {
      const queryParams: CardApprovalQueryParams = {
        startDate,
        endDate,
        cardNos,
        size,
        sortOrder: 'LATEST_FIRST',
      };
      return getCardApprovalByCursor({ companyId, queryParams });
    },
    refetchOnMount: true,
    enabled: !!companyId,
  });

  // 초기 데이터가 로드되면 상태 설정
  useEffect(() => {
    if (initialQuery.data?.Data?.data) {
      const data = initialQuery.data.Data.data;
      setAllCardApprovals(data.content);
      setNextCursor(data.nextCursor || null);
      setHasNext(data.hasNext);
    }
  }, [initialQuery.data]);

  const loadMoreCardApprovals = useCallback(async () => {
    if (!companyId || !nextCursor || !hasNext || isLoadingMore) return;
    mixpanel.track(MIXPANEL_EVENT.MOBILE.SCROLL_LOAD_MORE, {
      direction: 'up',
    });
    setIsLoadingMore(true);
    try {
      const queryParams: CardApprovalQueryParams = {
        startDate: withNoDate ? undefined : startDate,
        endDate: withNoDate ? undefined : endDate,
        cardNos,
        size,
        sortOrder: 'LATEST_FIRST',
        cursorUsedAt: nextCursor.usedAt,
        cursorId: nextCursor.id.toString(),
      };

      const response = await getCardApprovalByCursor({ companyId, queryParams });

      if (response?.Data?.data) {
        const data = response.Data.data;
        setAllCardApprovals((prev) => [...prev, ...data.content]);
        setNextCursor(data.nextCursor || null);
        setHasNext(data.hasNext);
      }
    } catch (error) {
      console.error('Failed to load more card approvals:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [companyId, nextCursor, hasNext, isLoadingMore, cardNos, size]);

  // 쿼리 파라미터가 변경되면 모든 상태 리셋
  useEffect(() => {
    setAllCardApprovals([]);
    setNextCursor(null);
    setIsLoadingMore(false);
    setHasNext(false);
  }, [startDate, endDate, JSON.stringify(cardNos), size]);

  return {
    data: allCardApprovals,
    isLoading: initialQuery.isFetching,
    isLoadingMore,
    hasNext,
    loadMoreCardApprovals,
    error: initialQuery.error,
  };
}
